import { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Plus } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { keys } from '@/lib/queryKeys';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { FormField } from '@/components/ui/FormField';
import { Input, NativeSelect, Textarea } from '@/components/ui/Input';
import { RowActionsMenu } from '@/components/ui/RowActionsMenu';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { ErrorState, LoadingState } from '@/components/ui/States';
import { Switch } from '@/components/ui/Switch';
import { toast } from '@/hooks/useToast';
import { cn } from '@/lib/cn';
import {
  createSymptomField, deleteSymptomField, getSymptomFields, reorderSymptomFields, updateSymptomField,
  type SymptomFieldInput,
} from '@/features/symptom-form/api';
import { SymptomFormFields } from '@/features/symptom-form/SymptomFormFields';
import {
  FIELD_TYPE_LABELS, OPTION_TYPES, TEXT_TYPES, type SymptomField, type SymptomFieldType, type SymptomValue,
} from '@/features/symptom-form/symptomForm';

/**
 * Admin → Clinical → Vitals Form Customization (admin:form-customization): the
 * fields of the Vitals stage's Symptoms card, in order. Consultation's
 * "Why today" shows what was answered. Backend: routes/symptom-form.js.
 *
 * Every change saves at once and applies to the next Vitals screen opened.
 * Visits already recorded keep their own copy of what was asked and
 * answered, so renaming, disabling or removing a field never alters them.
 */
export default function FormCustomizationTab() {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: keys.config.symptomForm(true), queryFn: () => getSymptomFields(true) });
  const fields = query.data ?? [];
  const [editing, setEditing] = useState<SymptomField | 'new' | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<SymptomField | null>(null);
  const [busy, setBusy] = useState(false);

  // Both the admin list (true) and the Vitals stage's enabled-only list (false).
  const refresh = () => qc.invalidateQueries({ queryKey: keys.config.symptomForm(true).slice(0, 2) });

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try { await fn(); toast({ title: ok, variant: 'success' }); }
    catch (e: any) { toast({ title: 'Not saved', description: e.message, variant: 'danger' }); }
    finally { await refresh(); setBusy(false); }
  };

  const move = (idx: number, dir: -1 | 1) => {
    const next = [...fields];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target]!, next[idx]!];
    qc.setQueryData(keys.config.symptomForm(true), next);
    act(() => reorderSymptomFields(next.map((f) => f.id)), 'Order saved');
  };

  if (query.isLoading) return <LoadingState label="Loading symptom fields…" />;
  if (query.isError) return <ErrorState message={(query.error as Error).message} onRetry={() => query.refetch()} />;

  const enabledCount = fields.filter((f) => f.enabled).length;

  return (
    <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,1fr)_400px]">
      <section aria-label="Vitals symptom fields">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-[14px] font-semibold text-ink">Vitals → Symptoms</h2>
            <p className="mt-0.5 text-[12.5px] text-ink-muted">
              {fields.length} field{fields.length === 1 ? '' : 's'}, {enabledCount} shown. Changes apply to the next Vitals screen opened; recorded visits keep what they recorded.
            </p>
          </div>
          <Button onClick={() => setEditing('new')} disabled={busy}><Plus className="h-4 w-4" /> Add field</Button>
        </div>

        <ol className="m-0 flex list-none flex-col gap-1.5 p-0">
          {fields.map((f, idx) => (
            <li key={f.id} className={cn('flex items-center gap-3 rounded-[var(--radius)] border border-border bg-card px-3 py-2.5', !f.enabled && 'bg-muted/50')}>
              <div className="flex flex-col">
                <button type="button" aria-label={`Move ${f.label} up`} disabled={idx === 0 || busy} onClick={() => move(idx, -1)}
                  className="flex h-5 w-6 items-center justify-center bg-transparent p-0 text-muted-foreground hover:text-ink disabled:opacity-30">
                  <ArrowUp className="h-3.5 w-3.5" />
                </button>
                <button type="button" aria-label={`Move ${f.label} down`} disabled={idx === fields.length - 1 || busy} onClick={() => move(idx, 1)}
                  className="flex h-5 w-6 items-center justify-center bg-transparent p-0 text-muted-foreground hover:text-ink disabled:opacity-30">
                  <ArrowDown className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className={cn('text-[13.5px] font-semibold', f.enabled ? 'text-ink' : 'text-ink-muted')}>{f.label}</span>
                  {f.mandatory && <Badge variant="warn">Required</Badge>}
                  {f.is_system && <Badge variant="info">Headline symptoms</Badge>}
                  {!f.enabled && <Badge>Hidden</Badge>}
                </div>
                <div className="mt-0.5 truncate text-[12px] text-ink-muted">
                  {FIELD_TYPE_LABELS[f.field_type]}
                  {OPTION_TYPES.includes(f.field_type) && ` · ${f.options.length} option${f.options.length === 1 ? '' : 's'}: ${f.options.slice(0, 4).join(', ')}${f.options.length > 4 ? '…' : ''}`}
                  {f.allow_custom && ' · staff can add their own'}
                  {f.max_length != null && ` · max ${f.max_length} characters`}
                </div>
              </div>
              <Switch
                checked={f.enabled}
                disabled={busy}
                aria-label={f.enabled ? `Hide ${f.label}` : `Show ${f.label}`}
                onCheckedChange={(c) => act(() => updateSymptomField(f.id, { enabled: c }), c ? `${f.label} shown` : `${f.label} hidden`)}
              />
              <RowActionsMenu
                label={`Actions for ${f.label}`}
                actions={[
                  { label: 'Edit…', onSelect: () => setEditing(f) },
                  { label: f.mandatory ? 'Make optional' : 'Make required',
                    onSelect: () => act(() => updateSymptomField(f.id, { mandatory: !f.mandatory }), f.mandatory ? `${f.label} is optional` : `${f.label} is required`) },
                  { label: 'Remove…', danger: true, disabled: f.is_system,
                    hint: f.is_system ? 'Hide it instead — Why today shows these as the headline' : undefined,
                    onSelect: () => setConfirmRemove(f) },
                ]}
              />
            </li>
          ))}
        </ol>
        {fields.length === 0 && (
          <div className="rounded-[var(--radius)] border border-dashed border-border p-6 text-center text-[13px] text-ink-muted">
            No fields — Vitals shows a single free-text Symptoms box until one is added.
          </div>
        )}
      </section>

      <PreviewPanel fields={fields.filter((f) => f.enabled)} />

      <FieldSheet
        field={editing}
        onClose={() => setEditing(null)}
        onSaved={async (msg) => { setEditing(null); toast({ title: msg, variant: 'success' }); await refresh(); }}
      />

      <ConfirmDialog
        open={!!confirmRemove}
        onOpenChange={(o) => { if (!o) setConfirmRemove(null); }}
        title={`Remove ${confirmRemove?.label ?? ''}?`}
        description="It will no longer be asked at Vitals. Visits that already recorded it keep their answer. To stop asking without removing it, hide it instead."
        confirmLabel="Remove"
        onConfirm={() => { const f = confirmRemove; if (f) act(() => deleteSymptomField(f.id), `${f.label} removed`); }}
      />
    </div>
  );
}

/** The Symptoms card as Vitals will render it — try it; nothing is saved. */
function PreviewPanel({ fields }: { fields: SymptomField[] }) {
  const [values, setValues] = useState<Record<string, SymptomValue>>({});
  return (
    <aside aria-label="Preview" className="rounded-xl border border-border bg-card xl:sticky xl:top-0">
      <div className="border-b border-border bg-muted px-3.5 py-2 text-[12px] font-semibold text-ink-muted">
        Preview — Vitals “Symptoms” card <span className="font-normal">(not saved)</span>
      </div>
      <div className="p-3.5">
        {fields.length
          ? <SymptomFormFields fields={fields} values={values} onChange={(k, v) => setValues((p) => ({ ...p, [k]: v }))} />
          : <div className="text-[12.5px] text-ink-muted">Nothing is shown — every field is hidden.</div>}
      </div>
    </aside>
  );
}

const TYPE_ORDER: SymptomFieldType[] = ['multiselect', 'checkboxes', 'dropdown', 'radio', 'checkbox', 'text', 'textarea', 'number', 'date'];

function FieldSheet({ field, onClose, onSaved }: { field: SymptomField | 'new' | null; onClose: () => void; onSaved: (msg: string) => void }) {
  const isNew = field === 'new';
  const existing = field && field !== 'new' ? field : null;
  const [label, setLabel] = useState('');
  const [type, setType] = useState<SymptomFieldType>('dropdown');
  const [optionsText, setOptionsText] = useState('');
  const [placeholder, setPlaceholder] = useState('');
  const [helpText, setHelpText] = useState('');
  const [maxLength, setMaxLength] = useState('');
  const [allowCustom, setAllowCustom] = useState(false);
  const [mandatory, setMandatory] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!field) return;
    setTouched(false); setError('');
    const f = field === 'new' ? null : field;
    setLabel(f?.label ?? '');
    setType(f?.field_type ?? 'dropdown');
    setOptionsText((f?.options ?? []).join('\n'));
    setPlaceholder(f?.placeholder ?? '');
    setHelpText(f?.help_text ?? '');
    setMaxLength(f?.max_length != null ? String(f.max_length) : '');
    setAllowCustom(f?.allow_custom ?? false);
    setMandatory(f?.mandatory ?? false);
    setEnabled(f?.enabled ?? true);
  }, [field]);

  const hasOptions = OPTION_TYPES.includes(type);
  const options = useMemo(() => optionsText.split('\n').map((o) => o.trim()).filter(Boolean), [optionsText]);
  const labelError = touched && !label.trim() ? 'Enter a label' : undefined;
  const optionsError = touched && hasOptions && !options.length && !(type === 'multiselect' && allowCustom) ? 'Add at least one option' : undefined;
  // Character limit: Short text / Long text only; blank = no limit.
  const isText = TEXT_TYPES.includes(type);
  const maxLengthNum = maxLength.trim() === '' ? null : Number(maxLength);
  const maxLengthBad = isText && maxLengthNum !== null && (!Number.isInteger(maxLengthNum) || maxLengthNum < 1 || maxLengthNum > 1000);
  const maxLengthError = touched && maxLengthBad ? 'Enter a whole number from 1 to 1000, or leave it blank' : undefined;

  const save = async () => {
    setTouched(true);
    if (!label.trim() || (hasOptions && !options.length && !(type === 'multiselect' && allowCustom)) || maxLengthBad) return;
    const body: SymptomFieldInput = {
      label: label.trim(), field_type: type, options: hasOptions ? options : [],
      placeholder: placeholder.trim() || null, help_text: helpText.trim() || null,
      max_length: isText ? maxLengthNum : null,
      allow_custom: type === 'multiselect' ? allowCustom : false, mandatory, enabled,
    };
    setSaving(true); setError('');
    try {
      if (isNew) { await createSymptomField(body); onSaved(`${body.label} added`); }
      else if (existing) { await updateSymptomField(existing.id, body); onSaved(`${body.label} saved`); }
    } catch (e: any) { setError(e.message); }
    setSaving(false);
  };

  return (
    <Sheet open={!!field} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{isNew ? 'Add symptom field' : `Edit ${existing?.label ?? ''}`}</SheetTitle>
          <SheetDescription>Shown on the Vitals stage’s Symptoms card. Recorded visits keep what they recorded.</SheetDescription>
        </SheetHeader>
        <SheetBody>
          {error && <div className="rounded-md bg-danger-soft px-3 py-2 text-[12.5px] text-danger-ink">{error}</div>}
          <FormField label="Label" required error={labelError} htmlFor="sf-label">
            <Input id="sf-label" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={120} placeholder="e.g. Duration" autoFocus />
          </FormField>
          <FormField label="Input type" htmlFor="sf-type"
            hint={existing?.is_system ? 'The headline symptoms picker is always a searchable multi-select.' : undefined}>
            <NativeSelect id="sf-type" className="w-full" value={type} disabled={!!existing?.is_system}
              onChange={(e) => setType(e.target.value as SymptomFieldType)}>
              {TYPE_ORDER.map((t) => <option key={t} value={t}>{FIELD_TYPE_LABELS[t]}</option>)}
            </NativeSelect>
          </FormField>
          {hasOptions && (
            <FormField label="Options" required={!(type === 'multiselect' && allowCustom)} error={optionsError} htmlFor="sf-options"
              hint={`One per line, in the order staff should see them. ${options.length} option${options.length === 1 ? '' : 's'}.`}>
              <Textarea id="sf-options" rows={8} value={optionsText} onChange={(e) => setOptionsText(e.target.value)}
                placeholder={'Fever\nHeadache\nCough'} />
            </FormField>
          )}
          {type === 'multiselect' && (
            <label className="flex items-center gap-2 text-[13px]">
              <Checkbox checked={allowCustom} onCheckedChange={(c) => setAllowCustom(c === true)} />
              Staff can add an entry that isn’t in the list
            </label>
          )}
          {type !== 'checkbox' && type !== 'radio' && type !== 'checkboxes' && type !== 'date' && (
            <FormField label="Placeholder" htmlFor="sf-placeholder">
              <Input id="sf-placeholder" value={placeholder} maxLength={200} onChange={(e) => setPlaceholder(e.target.value)} />
            </FormField>
          )}
          {isText && (
            <FormField label="Maximum characters" htmlFor="sf-maxlength" error={maxLengthError}
              hint="Staff can’t type more than this, and a counter shows how many are left. Leave blank for no limit (up to 1000).">
              <Input id="sf-maxlength" type="number" inputMode="numeric" min={1} max={1000} step={1}
                value={maxLength} onChange={(e) => setMaxLength(e.target.value)} placeholder="e.g. 20" />
            </FormField>
          )}
          <FormField label="Help text" htmlFor="sf-help" hint="A short line under the field. Not shown to doctors.">
            <Input id="sf-help" value={helpText} maxLength={300} onChange={(e) => setHelpText(e.target.value)} />
          </FormField>
          <label className="flex items-center gap-2 text-[13px]">
            <Checkbox checked={mandatory} onCheckedChange={(c) => setMandatory(c === true)} />
            Required — Vitals can’t be saved without it
          </label>
          <label className="flex items-center gap-2 text-[13px]">
            <Checkbox checked={enabled} onCheckedChange={(c) => setEnabled(c === true)} />
            Shown on the Vitals screen
          </label>
        </SheetBody>
        <SheetFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} loading={saving}>{isNew ? 'Add field' : 'Save'}</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
